import "../scss/app.scss";

import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(CustomEase, ScrollTrigger);
CustomEase.create("jitterSmooth", "0.5, 0, 0, 1");
CustomEase.create("jitterAccelerate", "1, 0, 1, 1");

import Home from "./renderers/home";
import Page from "./renderers/page";
import Contact from "./renderers/contact";

import CategoriesPush from "./components/global/categories-push";
import TitleSubtitleCtaPush from "./components/global/title-subtitle-cta-push";
import HomepageHero from "./components/home/homepage-hero";
import ProductCardsSlider from "./components/global/product-cards-slider";
import CollectionCardsHighlight from "./components/global/collection-cards-highlight";
import CollectionCard from "./components/global/collection-card";
import FeaturingLogos from "./components/home/featuring-logos";
import FreeProductsPush from "./components/home/free-products-push";
import CreatorsPush from "./components/home/creators-push";
import Cta from "./components/elements/cta";
import MarqueeBtn from "./components/elements/marquee-btn";
import NewsletterModalPush from "./components/elements/newsletter-modal-push";
import LifetimeStickyPush from "./components/elements/lifetime-sticky-push";
import ModalProductPush from "./components/elements/modal-product-push";
import ModalCollectionPush from "./components/elements/modal-collection-push";
import IconTitle from "./components/elements/icon-title";
import IconsList from "./components/elements/icons-list";
import StoryPush from "./components/home/story-push";
import Faq from "./components/global/faq";
import VideoPlayer from "./components/global/video-player";
import Anim from "./components/global/anim";
import { initLiveDemoEvents, initPageEvents, initItemListEvents } from "./utils/data-layer";
import LifetimePush from "./components/global/lifetime-push";
import Footer from "./components/global/footer";
import ContactForm from "./components/global/contact-form";
import Navigation from "./components/global/navigation";
import Loader from "./components/global/loader";
import { pageReady } from "./utils/page-ready";
import CursorTrail from "./components/global/cursor-trail";
import PixelOverlay from "./components/global/pixel-overlay";
import PixelReveal from "./components/global/pixel-reveal";
import PageTransition from "./components/global/page-transition";
import TextReveal from "./components/elements/text-reveal";
import AuthModal from "./components/elements/auth-modal";
import DownloadModal from "./components/elements/download-modal";
import SideCart from "./components/cart/side-cart";
import initCartCoupon from "./components/cart/cart-coupon";
import initCartLifetime from "./components/cart/cart-add-lifetime";
import initCartQuantity from "./components/cart/cart-quantity";
import initCartAddGrouped from "./components/cart/cart-add-grouped";
import initCartSingle from "./components/cart/cart-single";
import superscriptCopyright from "./utils/superscriptCopyright";
import { mountCardMedia } from "./utils/cardMediaPool";
import { mountBgVideos } from "./utils/bgVideo";

if (import.meta.env.DEV) {
  // Hand off from the dev-only <link> (see StylesScript.php) to Vite's own
  // hot-reloadable stylesheet, now that the scss import above has injected it.
  document.getElementById("vite-fouc-fix-css")?.remove();
}

// Page-specific components are `() => import()` loaders instead of classes, so
// their code (and ogl for the WebGL community push) stays out of the main
// bundle and is only fetched on pages that actually render them.
const components = {
  cta: Cta,
  navigation: Navigation,
  loader: Loader,
  "categories-push": CategoriesPush,
  "title-subtitle-cta-push": TitleSubtitleCtaPush,
  "homepage-hero": HomepageHero,
  "collections-hero": () => import("./components/pages/collections"),
  "product-cards-slider": ProductCardsSlider,
  "collection-cards-highlight": CollectionCardsHighlight,
  "collection-card": CollectionCard,
  "featuring-logos": FeaturingLogos,
  "free-products-push": FreeProductsPush,
  "community-push": () => import("./components/home/community-push"),
  "creators-push": CreatorsPush,
  "marquee-btn": MarqueeBtn,
  "newsletter-modal-push": NewsletterModalPush,
  "lifetime-sticky-push": LifetimeStickyPush,
  "modal-product-push": ModalProductPush,
  "modal-collection-push": ModalCollectionPush,
  "icon-title": IconTitle,
  "icons-list": IconsList,
  "story-push": StoryPush,
  faq: Faq,
  "video-player": VideoPlayer,
  "team-section": () => import("./components/about/team-section"),
  "images-stack": () => import("./components/pages/images-stack"),
  "collection-grid": () => import("./components/pages/collection-grid"),
  "collection-header": () => import("./components/pages/collection-header"),
  hiw: () => import("./components/pages/hiw"),
  products: () => import("./components/pages/products"),
  search: () => import("./components/pages/search"),
  "rotating-title": () => import("./components/pages/rotating-title"),
  "page-success": () => import("./components/pages/page-success"),
  "membership-plan-hero": () => import("./components/pages/membership-plan-hero"),
  "early-push": () => import("./components/pages/early-push"),
  "lifetime-push": LifetimePush,
  "membership-plan-table": () => import("./components/pages/membership-plan-table"),
  product: () => import("./components/pages/product"),
  footer: Footer,
  "contact-form": ContactForm,
  "showcase-gallery": () => import("./components/showcase/showcase-gallery"),
  showcase: () => import("./components/showcase/showcase"),
  "not-found-bg": () => import("./components/pages/404"),
  "account-navigation": () => import("./components/account/account-navigation"),
  "legal-navigation": () => import("./components/pages/legal-navigation"),
  "legal-subnav": () => import("./components/pages/legal-subnav"),
  "auth-modal": AuthModal,
  "download-modal": DownloadModal,
  "side-cart": SideCart,
};

function GSAPInit() {
  gsap.utils.mapRangeClamped = (inMin, inMax, outMin, outMax, value) => {
    const reversed = outMin > outMax
    return gsap.utils.clamp(
      reversed ? outMax : outMin,
      reversed ? outMin : outMax,
      gsap.utils.mapRange(
        inMin,
        inMax,
        outMin,
        outMax,
        value
      )
    )
  }

  gsap.ticker.delta = 0
  gsap.ticker.deltaMille = 0
  gsap.ticker.prevTime = 0

  gsap.ticker.add(() => {
    gsap.ticker.delta = gsap.ticker.time - gsap.ticker.prevTime
    gsap.ticker.deltaMille = gsap.ticker.delta * 1000
    gsap.ticker.prevTime = gsap.ticker.time
  })
}

// `?stats` turns the perf overlay on for the tab session (it survives internal
// navigation), `?stats=0` turns it off.
function perfDebugEnabled() {
  const param = new URLSearchParams(window.location.search).get("stats");
  try {
    if (param === "0") sessionStorage.removeItem("vs-stats");
    else if (param !== null) sessionStorage.setItem("vs-stats", "1");
    return sessionStorage.getItem("vs-stats") === "1";
  } catch (e) {
    return param !== null && param !== "0";
  }
}

if (perfDebugEnabled()) {
  import("./utils/perf-debug").then(({ default: initPerfDebug }) => {
    if (document.body) initPerfDebug();
    else document.addEventListener("DOMContentLoaded", initPerfDebug);
  });
}

// Real-user Core Web Vitals -> dataLayer (see utils/web-vitals.js). Its own
// chunk, requested once the page is idle so it never competes with the boot.
const loadWebVitals = () =>
  import("./utils/web-vitals")
    .then(({ default: initWebVitals }) => initWebVitals())
    .catch(() => {});
if ("requestIdleCallback" in window) requestIdleCallback(loadWebVitals, { timeout: 3000 });
else setTimeout(loadWebVitals, 1000);

// Held until the components below exist (so they can register their own blockers) and
// the fonts are in: entrance animations wait on pageReady.ready, not on mount.
const boot = pageReady.block("boot");

// Module scripts run once the document is parsed, so the lazy chunks the page
// needs can be requested right away, in parallel with the rest of the boot.
const isLazy = (entry) => !entry.prototype;
const lazyComponents = Object.fromEntries(
  Object.entries(components)
    .filter(([name, entry]) => isLazy(entry) && document.querySelector(`[data-component="${name}"]`))
    .map(([name, load]) => [name, load().then((m) => m.default)]),
);
const checkoutLoaded = document.body?.classList.contains("woocommerce-checkout")
  ? import("./components/checkout/checkout").then((m) => m.default)
  : null;

document.addEventListener("DOMContentLoaded", async () => {
  GSAPInit();

  Anim.init();
  CursorTrail.init();
  PixelOverlay.init();
  PageTransition.init();
  PixelReveal.init();
  TextReveal.init();
  superscriptCopyright();

  // Same order as before the split: every component class is resolved first,
  // then instantiated in the order of the map above.
  const resolved = await Promise.all(
    Object.entries(components).map(async ([name, entry]) => {
      if (!isLazy(entry)) return [name, entry];
      if (!lazyComponents[name]) return [name, null];
      try {
        return [name, await lazyComponents[name]];
      } catch (err) {
        console.warn(`[app] failed to load component "${name}":`, err);
        return [name, null];
      }
    }),
  );

  if (checkoutLoaded) {
    const Checkout = await checkoutLoaded;
    new Checkout().init();
  }

  initCartCoupon();
  initCartLifetime();
  initCartQuantity();
  initCartAddGrouped();
  initCartSingle();
  initLiveDemoEvents();
  initPageEvents();
  initItemListEvents();

  resolved.forEach(([name, Component]) => {
    if (!Component) return;
    document.querySelectorAll(`[data-component="${name}"]`).forEach((el) => {
      new Component(el);
    });
  });

  // Loaded dynamically (and caught) rather than statically imported above: some
  // ad-blocker/privacy-extension filter lists block requests whose path contains
  // "cookie-consent" by name (hence the neutral "vs-privacy" name). A static import of a blocked module fails the whole
  // app.js module graph — nothing in this file would run at all. A dynamic import
  // isolates that failure to just the banner not appearing. Runs after the loop
  // above on purpose, so it always registers into NotifStack last (see that
  // module) and stacks above lifetime-sticky-push / modal-product-push.
  document.querySelectorAll('[data-component="vs-privacy"]').forEach((el) => {
    import("./components/elements/vs-privacy")
      .then(({ default: VsPrivacy }) => new VsPrivacy(el))
      .catch((err) => console.warn("[vs-privacy] failed to load, likely blocked by the browser:", err));
  });

  // Context controllers (products, sliders, collection grid) claim their own
  // cards above; this picks up any product card left over (search, mobile
  // fallback lists) and drives its media from the shared pool.
  mountCardMedia(document);

  // Background <video data-src> nobody claimed above (page-success,
  // page-about's title section, ...) — see src/js/utils/bgVideo.js.
  mountBgVideos(document);

  // Fonts first, then the page: `boot` is the last thing pageReady waits on. The
  // [data-text-mask] / [data-prevent-flicker] content stays hidden (gsap-not-loaded)
  // until the page is uncovered and Anim has set up its initial states.
  document.fonts.ready.then(() => boot.release());
  pageReady.ready.then(() => {
    requestAnimationFrame(() => {
      document.body.classList.remove('gsap-not-loaded')
    })
  })
});

document.addEventListener("DOMContentLoaded", () => {
  const classes = document.body.classList;
  switch (true) {
    case classes.contains("home"):
      new Home();
      break;

    case classes.contains("page-template-contact"):
      new Contact();
      break;

    default:
      new Page();
  }
});
